import isFunction from "lodash/isFunction";
import startCase from "lodash/startCase";
import { Trash2 } from "lucide-react";

import { EVENT_ENTRIES } from "components/LogItem/events";
import { CALL_ACTIONS_WITH_OWN_ENTRY } from "components/LogItem/events/callEvents";
import { ViewResponseAction } from "components/LogItem/events/formEvents";
import { getMessagePart } from "components/LogItem/messages/emailMessagePart";

// A call's dispositions all render through the generic "call" entry. The
// actions that describe something done to the call rather than its outcome
// keep their own.
function entryKey(item) {
  if (
    item.category === "call" &&
    !CALL_ACTIONS_WITH_OWN_ENTRY.has(item.action)
  ) {
    return item.category;
  }
  // A campaign's own status change shares its action with a member's status
  // change within the campaign; only the category tells them apart.
  if (
    item.category === "campaign" &&
    item.action === "campaign_status_changed"
  ) {
    return "campaign_own_status_changed";
  }
  return item.action;
}

// lucide icons are forwardRef objects, not functions, so an entry's `icon` can
// be either a component or a function of the item that returns one.
const resolve = (attribute, item) =>
  isFunction(attribute) ? attribute(item) : attribute;

// A form response's creation snapshot hides its raw payload, so the card offers
// the answers through the same dialog as the "Form Submitted" entry.
const isFormResponseCreated = item =>
  item.action === "created" &&
  item.auditable_type === "CampaignTargetFormResponse";

/**
 * Resolves the title, icon component, message and actions component for an
 * audit entry.
 *
 * @param {object} item The audit log entry.
 * @returns {{title: *, Icon: ?object, message: *, actions: ?Function}}
 */
export function resolveEventEntry(item) {
  const key = entryKey(item);
  const entry = EVENT_ENTRIES[key] || {};

  // A deleted call reads as any other deleted record, under its own title.
  if (item.action === "deleted" && item.category === "call") {
    return {
      title: "Call Deleted",
      Icon: Trash2,
      message: EVENT_ENTRIES.deleted.message(item),
      actions: entry.actions,
    };
  }

  return {
    title: resolve(entry.title, item) || startCase(key),
    Icon: resolve(entry.icon, item),
    message:
      resolve(entry.message, item) ||
      getMessagePart(item.info.message, "detail"),
    actions: isFormResponseCreated(item) ? ViewResponseAction : entry.actions,
  };
}
